import * as React from 'react';

import * as ramda from 'ramda';

import {Routing} from 'nutshell-core/routing';
import type {FilterObject} from 'nutshell-core/types';

// @ts-expect-error
import {AudienceIcon} from 'shells/icon/svg/audience-icon';
import {Link} from 'shells/link/link';
import type {TwoLineOptionWithIcon} from 'shells/select-two-line-icon/select-two-line-icon';

import type {
    GetProspectorContacts_prospectorContacts_mcfxContacts as GetProspectorContacts,
    CreateProspectorContacts_prospectorContactsAdd_contacts as CreatedProspectorContacts,
    CreateProspectorContacts,
    GetProspectorSavedFilters_prospectorSavedFilters as SavedFilter,
} from 'nutshell-graphql-types';

import type {UnlockInfo} from '../../../people-iq/graphql/hooks/use-get-unlock-info';

import {appHistory} from '../../../../history';
import {addNotification} from '../../../../notifications/index';

import {DEFAULT_PAGE_SIZE} from './prospector-page';

const DEFAULT_FILTERS = ['hasEmail', 'hasLinkedin', 'hasPhoneNumber'];

export function containsProspectorContact(
    selectedProspectorContacts: GetProspectorContacts[],
    prospectorContact: GetProspectorContacts
): boolean {
    return Boolean(
        selectedProspectorContacts.find(
            (selectedProspectorContact) => selectedProspectorContact.id === prospectorContact.id
        )
    );
}

export function getPopoverTitle(selectedProspectorContacts: GetProspectorContacts[]): string {
    const numSelectedProspectorContacts = selectedProspectorContacts.length;
    if (numSelectedProspectorContacts === 0) return '';

    const firstSelected = selectedProspectorContacts[0];
    // @ts-expect-error
    const fullName = firstSelected.fullName;

    if (numSelectedProspectorContacts === 1) {
        return `Add ${fullName}`;
    } else if (numSelectedProspectorContacts === 2) {
        return `Add ${fullName} and 1 other`;
    } else {
        return `Add ${fullName} and ${numSelectedProspectorContacts - 1} others`;
    }
}

export function getPopoverSubtitle(
    unlockInfo: UnlockInfo | null | undefined,
    numSelectedProspectorContacts: number
): React.ReactElement<any> | null | undefined {
    if (!unlockInfo) return null;

    const {unlocksRemaining, unlockLimit} = unlockInfo;

    if (unlocksRemaining === 0) {
        return (
            <>
                All {unlockLimit} credits have been used this month. Come back on the 1st or{' '}
                <Link href='/setup/billing/sales' variant='primary' newTab={true}>
                    purchase additional credits
                </Link>{' '}
                to get more now.
            </>
        );
    } else if (unlocksRemaining - numSelectedProspectorContacts < 0) {
        return (
            <>
                You can use {unlocksRemaining} more {unlocksRemaining === 1 ? 'credit' : 'credits'}{' '}
                out of the {unlockLimit} available in your plan this month. Come back on the 1st or{' '}
                <Link href='/setup/billing/sales' variant='primary' newTab={true}>
                    upgrade your ProspectorIQ plan
                </Link>{' '}
                to get more now.
            </>
        );
    } else if (unlocksRemaining - numSelectedProspectorContacts === 0) {
        return <>This will use all of your credits</>;
    }

    return (
        <>
            {unlocksRemaining === 1
                ? 'This will use your last credit'
                : `This will use ${numSelectedProspectorContacts} of your ${unlocksRemaining} credits`}
        </>
    );
}

export function notifyUser(data?: CreateProspectorContacts) {
    if (!data) {
        throw new Error('Response data is missing');
    }

    const savedProspects = data.prospectorContactsAdd.contacts;
    const bodyText = getSuccessNotificationBodyText(
        savedProspects,
        data.prospectorContactsAdd.numUpdatedContacts
    );

    addNotification('Success!', {
        body: bodyText,
        icon: 'check',
        onClick: () => {
            if (savedProspects.length === 1) {
                // @ts-expect-error
                appHistory.push(savedProspects[0].htmlUrlPath);
            } else if (savedProspects.length > 1) {
                // We need to trigger a page reload to ensure that the prospector origin is present in the filter dropdown
                // @ts-expect-error
                window.location.href = buildProspectContactsUrl(savedProspects[0].origin.id);
            }
        },
    });
}

export function buildProspectContactsUrl(prospectorOrigin: string) {
    const urlParams = {
        fields: {
            contacts: 'name,createdTime,email,origin,phone,address,creator',
        },
        filter: {
            origin: {
                anyAll: 'any',
                data: [{data: prospectorOrigin}],
            },
        },
        sort: '-createdTime',
        view: 'list',
    } as const;

    const params = Routing.param(urlParams);

    return `/people?${params}`;
}

export function buildProspectLeadsUrl(prospectorOrigin: string) {
    const urlParams = {
        fields: {
            leads: 'description,createdTime,stagesets,owner,origin,creator',
        },
        filter: {
            origin: {
                anyAll: 'any',
                data: [{data: prospectorOrigin}],
            },
        },
        sort: '-createdTime',
        view: 'list',
    } as const;

    const params = Routing.param(urlParams);

    return `/leads?${params}`;
}

function getSuccessNotificationBodyText(
    savedProspects: CreatedProspectorContacts[],
    numUpdatedContacts: number
) {
    if (savedProspects.length === 0) {
        if (numUpdatedContacts === 1) {
            return '1 person was updated.';
        }

        return `${numUpdatedContacts} people were updated.`;
    }

    const firstSavedProspect = savedProspects[0];
    const updatedContactsPhrase = numUpdatedContacts === 1 ? 'person was' : 'people were';
    if (savedProspects.length === 1) {
        if (numUpdatedContacts === 0) {
            // @ts-expect-error
            return `${firstSavedProspect.name} was created.`;
        }

        // @ts-expect-error
        return `${firstSavedProspect.name} was created. ${numUpdatedContacts} ${updatedContactsPhrase} updated.`;
    }

    // 2+ saved prospects
    const numOtherContactsCreated = savedProspects.length - 1;
    if (numUpdatedContacts === 0) {
        // @ts-expect-error
        return `${firstSavedProspect.name} and ${numOtherContactsCreated} others were created.`;
    }

    // @ts-expect-error
    return `${firstSavedProspect.name} and ${numOtherContactsCreated} others were created. ${numUpdatedContacts} ${updatedContactsPhrase} updated.`;
}

export function savedFilterToOption(filter: SavedFilter): TwoLineOptionWithIcon {
    let name = 'unknown';

    if (filter.creatorUser && filter.creatorUser.name) {
        name = filter.creatorUser.name;
    }

    return {
        value: filter.id,
        label: filter.name,
        sublabel: `Created by ${name}`,
        icon: AudienceIcon,
    };
}

export function findMatchingFilter(urlFilters: FilterObject, savedFilters: SavedFilter[]) {
    return savedFilters.find((savedFilter) => {
        const filterObject = Routing.deparam(savedFilter.filterString);

        return areSameFilters(filterObject.filter, urlFilters.filter);
    });
}

export function areSameFilters(filter1: FilterObject, filter2: FilterObject) {
    return ramda.equals(filter1, filter2);
}

/**
 *
 * Removes any "must have" data filters (e.g. Must have email). If only the
 * must have filters are applied, we don't want to make any requests to the
 * server.
 *
 * @param {FilterObject[]} activeFilters
 * @returns {FilterObject[]} Filters that are not the default filters, and actually cause us to make
 * a request to the API if they exist
 *
 */
export function pruneMustHaveFilters(activeFilters: FilterObject[]): FilterObject[] {
    // @ts-expect-error
    return activeFilters.filter((filter) => !DEFAULT_FILTERS.includes(Object.keys(filter)[0]));
}

/**
 * Builds a filter object with the default filters if they exist in the active filters
 * @param {FilterObject[]} activeFilters
 * @returns {Object} Filter object with the default filters as the values
 */
export function buildDefaultFiltersObject(activeFilters: FilterObject[]): {
    filter: FilterObject;
} {
    const filters = DEFAULT_FILTERS.reduce<Record<string, any>>((acc, filter) => {
        const existingFilter = activeFilters.find((f) => Object.keys(f)[0] === filter);
        if (existingFilter) {
            acc[filter] = existingFilter[filter];
        }

        return acc;
    }, {});

    return {filter: filters};
}

// Note: Returns ProspectorFilters (object) but hard to type
export function convertFilterArrayToProspectorFiltersForApi(filters: FilterObject[]): any {
    return filters.reduce<Record<string, any>>((acc, filter) => {
        const key = Object.keys(filter)[0];
        // @ts-expect-error
        let value = filter[key];

        if (typeof value === 'object') {
            value = JSON.stringify(value);
        }

        return {
            ...acc,
            // @ts-expect-error
            [key]: value,
        };
    }, {});
}

export function calculateTotalPages(
    totalCount: number,
    numFetchedContacts: number,
    currentPage: number
) {
    let totalPages = Math.ceil(totalCount / DEFAULT_PAGE_SIZE);
    // if we have less than a full page of results, we know we are on the last page
    if (numFetchedContacts < DEFAULT_PAGE_SIZE) {
        totalPages = currentPage;
    }

    return totalPages;
}
